/**
 * 雛形のサブステップの「提出オフセット（分）」(spec AC-WFT-041 / AC-WFT-042) の
 * 表示条件・入力の解釈・wire への投影。
 *
 * `holdsPlanAutoSubmitOffset` は backend の `validatePlanAutoSubmitOffsetHolder` /
 * `linksAutoSubmittablePlan`
 * (go/internal/backend/domain/entity/workflow/sub_step_template.go) のミラー。
 * backend 側の条件を変えた時は本ファイルも同期する (approvalSetting.ts と同じ扱い)。
 *
 * form state は 2 field:
 * - `planAutoSubmitOffsetMinutes`: Get で読み込んだ値 (UI からは書かない)
 * - `planAutoSubmitOffsetInput`: 欄に打った生の文字列 (undefined = 未編集)
 *
 * feature flag は引数に取らない。flag は欄を出すかどうか
 * (`isPlanAutoSubmitOffsetFieldShown`。編集済みの行は flag に関わらず出す) だけに使い、
 * 検証と送信は kind / mode / 入力文字列だけで決める。
 * flag の再取得中に入力値が黙って読み込み値に戻るのを避けるため。
 */
import { ApprovalMode } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import { LinkedPageKind } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

export const PLAN_AUTO_SUBMIT_OFFSET_MAX_MINUTES = 1440

export type PlanAutoSubmitOffsetMinutes = number & {
  readonly __brand: 'PlanAutoSubmitOffsetMinutes'
}

export type PlanAutoSubmitOffsetResolution =
  /** kind / mode が holder でない。読み込み値をそのまま送る (消去は backend の discard)。 */
  | { kind: 'notHolder'; wireMinutes: number | undefined }
  /** holder・未編集。NULL は NULL のまま送り、表示だけ 0 にする。 */
  | { kind: 'untouched'; text: string; wireMinutes: number | undefined }
  /** holder・編集済み・妥当。 */
  | { kind: 'entered'; text: string; minutes: PlanAutoSubmitOffsetMinutes }
  /** holder・編集済み・不正。 */
  | { kind: 'invalid'; text: string }

export type PlanAutoSubmitOffsetRow = {
  linkedPageKind?: LinkedPageKind | undefined
  approvalSetting?: { mode: ApprovalMode } | undefined
  planAutoSubmitOffsetMinutes?: number | undefined
  planAutoSubmitOffsetInput?: string | undefined
}

export function holdsPlanAutoSubmitOffset(
  kind: LinkedPageKind | undefined,
  mode: ApprovalMode | undefined,
): boolean {
  return (
    (kind === LinkedPageKind.PLAN_PROCUREMENT ||
      kind === LinkedPageKind.PLAN_GEN_SALES) &&
    mode === ApprovalMode.AUTO
  )
}

/** trim 後に 0 以上の整数表記で 1440 以下のものだけ受理する。 */
export function parsePlanAutoSubmitOffset(
  text: string,
): PlanAutoSubmitOffsetMinutes | undefined {
  const trimmed = text.trim()
  if (!/^\d+$/.test(trimmed)) return undefined
  const n = Number(trimmed)
  if (n > PLAN_AUTO_SUBMIT_OFFSET_MAX_MINUTES) return undefined
  return n as PlanAutoSubmitOffsetMinutes
}

export function resolvePlanAutoSubmitOffset(
  row: PlanAutoSubmitOffsetRow,
): PlanAutoSubmitOffsetResolution {
  const loaded = row.planAutoSubmitOffsetMinutes
  if (
    !holdsPlanAutoSubmitOffset(row.linkedPageKind, row.approvalSetting?.mode)
  ) {
    return { kind: 'notHolder', wireMinutes: loaded }
  }
  const input = row.planAutoSubmitOffsetInput
  if (input === undefined) {
    return {
      kind: 'untouched',
      text: String(loaded ?? 0),
      wireMinutes: loaded,
    }
  }
  const minutes = parsePlanAutoSubmitOffset(input)
  return minutes === undefined
    ? { kind: 'invalid', text: input }
    : { kind: 'entered', text: input, minutes }
}

/** Update / Create request に載せる値。不正値は決して送らない。 */
export function planAutoSubmitOffsetForWire(
  row: PlanAutoSubmitOffsetRow,
): number | undefined {
  const resolution = resolvePlanAutoSubmitOffset(row)
  switch (resolution.kind) {
    case 'notHolder':
    case 'untouched':
      return resolution.wireMinutes
    case 'entered':
      return resolution.minutes
    case 'invalid':
      return row.planAutoSubmitOffsetMinutes
  }
}

export function isPlanAutoSubmitOffsetFieldShown(
  resolution: PlanAutoSubmitOffsetResolution,
  automationAvailable: boolean,
): boolean {
  if (resolution.kind === 'notHolder') return false
  // 利用者が欄を編集済み (untouched 以外) の行は、flag の再取得中・失敗・OFF に
  // 転じても欄を残す。消すと AC-WFT-042 の文言が当該行に出ず、直す欄も無くなる。
  return automationAvailable || resolution.kind !== 'untouched'
}
